import { useTranslation } from "react-i18next";
import { TextField } from "@material-ui/core";
import { Autocomplete, createFilterOptions } from "@material-ui/lab";
import ModelHints from "./ModelHints";
import ModelOption from "./ModelOption";

import { AIModel, AIProvider } from "@dashboard-v3/api";

const filterOptions = createFilterOptions<AIModel>({
  stringify: option => `${option.name} ${option.id}`,
});

interface Props {
  provider: AIProvider;
  value: string;
  disabled: boolean;
  onChange: (model: string) => void;
}

export default function ModelField(props: Props) {
  const { provider, value, disabled, onChange } = props;
  const { t } = useTranslation("storage");
  const model = provider.models.find(option => option.id === value.trim());
  const label = provider.baseUrlRequired
    ? t("externalAI.form.deployment")
    : t("externalAI.form.model");

  function handleChange(_, selected: string | AIModel | null) {
    if (!selected) return onChange("");
    if (typeof selected === "string") return onChange(selected);
    return onChange(selected.id);
  }

  function handleInputChange(event: unknown, text: string) {
    if (!event) return;
    onChange(text);
  }

  return (
    <>
      <Autocomplete
        freeSolo
        options={provider.models}
        value={model || null}
        inputValue={value}
        disabled={disabled}
        filterOptions={filterOptions}
        getOptionLabel={getOptionLabel}
        getOptionSelected={isOptionSelected}
        onChange={handleChange}
        onInputChange={handleInputChange}
        renderOption={option => (
          <ModelOption
            model={option}
            isDefault={option.id === provider.defaultModel}
          />
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            fullWidth
            required
            inputProps={{
              ...params.inputProps,
              "data-testid": "external-ai__model",
            }}
          />
        )}
      />
      <ModelHints provider={provider} model={model} value={value} />
    </>
  );
}

function getOptionLabel(option: string | AIModel) {
  if (typeof option === "string") return option;
  return option.id;
}

function isOptionSelected(option: AIModel, selected: string | AIModel) {
  return option.id === getOptionLabel(selected);
}
